import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ── Homes ──────────────────────────────────────────────────────────────────────
export const getHomes = async () => {
  const { data, error } = await supabase
    .from('homes')
    .select('*, items(cost, status)')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export const getHome = async (id) => {
  const { data, error } = await supabase.from('homes').select('*').eq('id', id).single()
  if (error) throw error
  return data
}

export const createHome = async (home) => {
  const { data, error } = await supabase.from('homes').insert(home).select().single()
  if (error) throw error
  return data
}

export const updateHome = async (id, updates) => {
  const { data, error } = await supabase.from('homes').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export const deleteHome = async (id) => {
  const { error } = await supabase.from('homes').delete().eq('id', id)
  if (error) throw error
}

// ── Rooms ──────────────────────────────────────────────────────────────────────
export const getRooms = async (homeId) => {
  const { data, error } = await supabase
    .from('rooms')
    .select('*, items(cost, status)')
    .eq('home_id', homeId)
    .order('floor_level').order('name')
  if (error) throw error
  return data
}

export const getRoom = async (id) => {
  const { data, error } = await supabase.from('rooms').select('*').eq('id', id).single()
  if (error) throw error
  return data
}

export const createRoom = async (room) => {
  const { data, error } = await supabase.from('rooms').insert(room).select().single()
  if (error) throw error
  return data
}

export const updateRoom = async (id, updates) => {
  const { data, error } = await supabase.from('rooms').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export const deleteRoom = async (id) => {
  const { error } = await supabase.from('rooms').delete().eq('id', id)
  if (error) throw error
}

// ── Items ──────────────────────────────────────────────────────────────────────
export const getItems = async (roomId) => {
  const { data, error } = await supabase
    .from('items').select('*').eq('room_id', roomId).order('type').order('name')
  if (error) throw error
  return data
}

export const getItemsByHome = async (homeId) => {
  const { data, error } = await supabase
    .from('items')
    .select('*, rooms!inner(id, name, home_id)')
    .eq('rooms.home_id', homeId)
    .order('status').order('name')
  if (error) throw error
  return data
}

export const createItem = async (item) => {
  const { data, error } = await supabase.from('items').insert(item).select().single()
  if (error) throw error
  return data
}

export const updateItem = async (id, updates) => {
  const { data, error } = await supabase.from('items').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export const deleteItem = async (id) => {
  const { error } = await supabase.from('items').delete().eq('id', id)
  if (error) throw error
}

// ── Expenses ──────────────────────────────────────────────────────────────────
export const getExpenses = async (homeId) => {
  const { data, error } = await supabase
    .from('expenses').select('*').eq('home_id', homeId).order('next_due', { ascending: true })
  if (error) throw error
  return data
}

export const getRecurringExpensesByHome = async (homeId) => {
  const { data, error } = await supabase
    .from('expenses').select('*').eq('home_id', homeId).neq('period', 'One-Time')
    .order('next_due', { ascending: true })
  if (error) throw error
  return data
}

export const createExpense = async (expense) => {
  const { data, error } = await supabase.from('expenses').insert(expense).select().single()
  if (error) throw error
  return data
}

export const updateExpense = async (id, updates) => {
  const { data, error } = await supabase.from('expenses').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export const deleteExpense = async (id) => {
  const { error } = await supabase.from('expenses').delete().eq('id', id)
  if (error) throw error
}

// ── CSV export helper ─────────────────────────────────────────────────────────
export const downloadItemsCSV = (items, rooms, filename) => {
  const headers = ['Name','Type','Brand','Details','Model #','Color/Finish','Dimensions','Cost','Status','Date Purchased','Purchase URL','Warranty URL','Notes','Space']
  const rows = items.map(item => {
    const room = rooms.find(r => r.id === item.room_id)
    return [
      item.name, item.type||'', item.brand||'', item.details||'',
      item.model_number||'', item.color_finish||'', item.dimensions||'',
      item.cost||'', item.status||'', item.date_purchased||'',
      item.purchase_url||'', item.warranty_url||'', item.notes||'',
      room?.name||''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
  })
  const csv = [headers.join(','), ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename + '.csv'; a.click()
  URL.revokeObjectURL(url)
}

// ── Urgency helpers ───────────────────────────────────────────────────────────
export const daysUntil = (dateStr) => {
  if (!dateStr) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(dateStr)
  return Math.round((due - today) / (1000 * 60 * 60 * 24))
}

export const urgencyConfig = (days, dateStr) => {
  if (days === null) return { label: 'No date set', color: '#9E9E9E', bg: '#F5F5F5' }
  if (days < 0)  return { label: `${Math.abs(days)}d overdue`, color: '#B91C1C', bg: '#FEF2F2' }
  if (days === 0) return { label: 'Due today', color: '#B91C1C', bg: '#FEF2F2' }
  if (days <= 14) return { label: `Due in ${days}d`, color: '#B45309', bg: '#FFFBEB' }
  if (days <= 90) return { label: `Due in ${days}d`, color: '#15803D', bg: '#F0FDF4' }
  const d = new Date(dateStr)
  return {
    label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    color: '#6B7280', bg: '#F3F4F6'
  }
}
